import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import DomainAddIcon from "@mui/icons-material/DomainAdd";
import LockIcon from "@mui/icons-material/Lock";
import SecurityIcon from "@mui/icons-material/Security";
import Alert from "@mui/joy/Alert";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import Chip from "@mui/joy/Chip";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Grid from "@mui/joy/Grid";
import Input from "@mui/joy/Input";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalOverflow from "@mui/joy/ModalOverflow";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import React from "react";
import { Permission, getUserPermissions } from "../../../models/auth";
import TitleStylizedUnderline from "../../../shared/components/ui/TitleStylizedUnderline";
import { SimpleAuthenticationService } from "../../../shared/services/api/onprem/auth";
import { QuartzClient } from "../../../shared/services/api/quartz";
import { UserContext } from "../../admin/providers/UserProvider";

const permissionLabels: Record<Permission, string> = {
  [Permission.PATIENT]: "Patient Data",
  [Permission.DOCUMENT]: "Documents",
  [Permission.RPM]: "Remote Monitoring",
  [Permission.COHORT]: "Cohorts",
  [Permission.PROJECT]: "Projects & Studies",
  [Permission.ADMIN]: "Administration",
  [Permission.IDENTIFYING_DATA]: "Identifying Data",
};

const permissionDescriptions: Record<Permission, string> = {
  [Permission.PATIENT]: "Access to patient information and medical notes",
  [Permission.DOCUMENT]: "Upload, view, and manage documents",
  [Permission.RPM]: "Remote patient monitoring functionality",
  [Permission.COHORT]: "Create and manage patient cohorts",
  [Permission.PROJECT]: "Access to research projects and studies",
  [Permission.ADMIN]: "User management and system administration",
  [Permission.IDENTIFYING_DATA]: "Access to non-pseudonymized patient data",
};

export default function SimpleUserProfile() {
  const { user } = React.useContext(UserContext);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = React.useState(false);
  const [passwordForm, setPasswordForm] = React.useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = React.useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = React.useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = React.useState(false);

  if (!user) {
    return null;
  }

  const userPermissions = getUserPermissions(user.attributes);
  let firstPart = user.email.includes("/") ? user.email.split("/")[0] : "";
  let username = firstPart.split("@")[0];
  let rootPart = username.replace("-", " ").toUpperCase();
  let email = user.email.includes("/") ? user.email.split("/")[1] : user.email;

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 10) {
      errors.push("Password must be at least 10 characters long");
    }

    if (!/[a-zA-Z]/.test(password)) {
      errors.push("Password must contain at least one letter");
    }

    if (!/[0-9]/.test(password)) {
      errors.push("Password must contain at least one number");
    }

    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push("Password must contain at least one special character");
    }

    return errors;
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    // Validate password requirements
    const passwordErrors = validatePassword(passwordForm.newPassword);
    if (passwordErrors.length > 0) {
      setPasswordError(passwordErrors.join(". "));
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    setIsChangingPassword(true);

    try {
      const authHeader = await SimpleAuthenticationService.authorizationHeader();
      if (!authHeader) {
        setPasswordError("Not authenticated");
        return;
      }

      const quartzClient = new QuartzClient(window.CONFIG.backendUrl, async () => authHeader);

      // Use the new auth endpoint for changing own password instead of admin endpoint
      await quartzClient.auth.changeOwnPassword({
        user: email,
        password: passwordForm.newPassword,
      });

      setPasswordSuccess("Password changed successfully!");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setIsPasswordModalOpen(false);
        setPasswordSuccess(null);
      }, 2000);
    } catch (error) {
      console.error("Failed to change password:", error);
      setPasswordError("Failed to change password. Please try again.");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const resetPasswordModal = () => {
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordError(null);
    setPasswordSuccess(null);
    setShowPasswordRequirements(false);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Grid container spacing={4}>
        <Grid xs={12} md={6}>
          <Typography level="h2" sx={{ mb: 3 }}>
            <TitleStylizedUnderline>Personal Profile</TitleStylizedUnderline>
          </Typography>

          <Stack spacing={3}>
            {/* Domain Alert */}
            {rootPart !== "" && (
              <Alert variant="soft" color="success" startDecorator={<DomainAddIcon />}>
                <Box>
                  <Typography level="title-sm">Domain</Typography>
                  <Typography level="body-sm">{rootPart}</Typography>
                </Box>
              </Alert>
            )}

            {/* User Information Card */}
            <Card variant="outlined">
              <CardContent>
                <Typography
                  level="title-md"
                  startDecorator={<AccountCircleRoundedIcon />}
                  sx={{ mb: 2 }}
                >
                  User Information
                </Typography>
                <Stack spacing={2}>
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input value={email} disabled />
                  </FormControl>
                  <FormControl>
                    <FormLabel>First name</FormLabel>
                    <Input value={user.attributes.given_name} disabled />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Last name</FormLabel>
                    <Input value={user.attributes.name} disabled />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Organization</FormLabel>
                    <Input value={user.attributes.organization ?? ""} disabled />
                  </FormControl>
                </Stack>
              </CardContent>
            </Card>

            {/* Permissions Card */}
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" startDecorator={<SecurityIcon />} sx={{ mb: 2 }}>
                  Permissions
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {userPermissions.map((permission) => (
                      <Chip key={permission} variant="soft" color="primary" size="sm">
                        {permissionLabels[permission]}
                      </Chip>
                    ))}
                  </Box>
                  <Divider />
                  <Stack spacing={1}>
                    {userPermissions.map((permission) => (
                      <Box key={permission}>
                        <Typography level="body-sm" fontWeight="md">
                          {permissionLabels[permission]}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {permissionDescriptions[permission]}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Security Actions */}
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" startDecorator={<LockIcon />} sx={{ mb: 2 }}>
                  Security
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  startDecorator={<LockIcon />}
                  onClick={() => setIsPasswordModalOpen(true)}
                >
                  Change Password
                </Button>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Password Change Modal */}
      <Modal
        open={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          resetPasswordModal();
        }}
      >
        <ModalOverflow>
          <ModalDialog sx={{ width: 400 }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Change Password
            </Typography>

            <form onSubmit={handlePasswordSubmit}>
              <Stack spacing={2}>
                {passwordError && <Alert color="danger">{passwordError}</Alert>}
                {passwordSuccess && <Alert color="success">{passwordSuccess}</Alert>}

                <FormControl>
                  <FormLabel>Current Password</FormLabel>
                  <Input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                    }
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>New Password</FormLabel>
                  <Input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => {
                      setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                      setShowPasswordRequirements(true);
                    }}
                    onFocus={() => setShowPasswordRequirements(true)}
                    required
                  />
                  {showPasswordRequirements && passwordForm.newPassword && (
                    <Box sx={{ mt: 1 }}>
                      <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                        Password requirements:
                      </Typography>
                      <Stack spacing={0.5}>
                        <Typography
                          level="body-xs"
                          color={passwordForm.newPassword.length >= 10 ? "success" : "danger"}
                        >
                          ✓ At least 10 characters
                        </Typography>
                        <Typography
                          level="body-xs"
                          color={/[a-zA-Z]/.test(passwordForm.newPassword) ? "success" : "danger"}
                        >
                          ✓ At least one letter
                        </Typography>
                        <Typography
                          level="body-xs"
                          color={/[0-9]/.test(passwordForm.newPassword) ? "success" : "danger"}
                        >
                          ✓ At least one number
                        </Typography>
                        <Typography
                          level="body-xs"
                          color={
                            /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(passwordForm.newPassword)
                              ? "success"
                              : "danger"
                          }
                        >
                          ✓ At least one special character (!@#$%^&*...)
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel>Confirm New Password</FormLabel>
                  <Input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                    }
                    required
                  />
                  {passwordForm.confirmPassword &&
                    passwordForm.newPassword !== passwordForm.confirmPassword && (
                      <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                        Passwords do not match
                      </Typography>
                    )}
                  {passwordForm.confirmPassword &&
                    passwordForm.newPassword === passwordForm.confirmPassword && (
                      <Typography level="body-xs" color="success" sx={{ mt: 0.5 }}>
                        Passwords match ✓
                      </Typography>
                    )}
                </FormControl>

                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => {
                      setIsPasswordModalOpen(false);
                      resetPasswordModal();
                    }}
                    disabled={isChangingPassword}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isChangingPassword}
                    disabled={
                      !passwordForm.currentPassword ||
                      !passwordForm.newPassword ||
                      !passwordForm.confirmPassword
                    }
                  >
                    Change Password
                  </Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </ModalOverflow>
      </Modal>
    </Box>
  );
}
